import { useState, useEffect } from 'react';
import { Smartphone, RefreshCw, CheckCircle2, AlertCircle, Loader2, QrCode } from 'lucide-react';
import { useSalonId } from '../../../hooks/useSalonId';
import { whatsappApi } from '../../../api/whatsappApi';

type Status = 'DISCONNECTED' | 'QR_READY' | 'CONNECTED';

const WhatsAppIntegration = () => {
    const salonId = useSalonId();
    const [status, setStatus] = useState<Status>('DISCONNECTED');
    const [qrCode, setQrCode] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const checkStatus = async () => {
        if (!salonId) return;
        try {
            const data = await whatsappApi.getStatus(salonId);
            // Only update if status changed to prevent unnecessary re-renders
            // But we need to update if we are waiting for connection
            if (data.status && data.status !== status) {
                setStatus(data.status as Status);
                if (data.status === 'CONNECTED') {
                    setQrCode(null);
                }
            } else if (status === 'QR_READY' && data.status === 'CONNECTED') {
                // Explicitly handle transition from QR_READY to CONNECTED if status didn't catch it for some reason
                setStatus('CONNECTED');
                setQrCode(null);
            }
        } catch (err) {
            console.error('Failed to check status', err);
        }
    };

    // Initial check on mount
    useEffect(() => {
        checkStatus();
    }, [salonId]);

    // Polling effect
    useEffect(() => {
        let intervalId: any = null;

        if (status === 'QR_READY') {
            intervalId = setInterval(checkStatus, 3000);
        }

        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [status, salonId]);

    const handleConnect = async () => {
        if (!salonId) return;
        setIsLoading(true);
        setError(null);
        try {
            // First creates instance if needed, then gets QR
            const data = await whatsappApi.connect(salonId);
            setQrCode(data.qrcode);
            setStatus('QR_READY');
        } catch (err) {
            setError('No se pudo conectar con el Gateway. Intenta de nuevo.');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDisconnect = async () => {
        // Implement logout logic if backend supports it
        // For now just clear local state or call an endpoint
        alert('Para desconectar, cierra la sesión desde WhatsApp en tu celular (Dispositivos Vinculados).');
    };

    return (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-emerald-50 to-white">
                <div className="flex items-center gap-3">
                    <Smartphone className="w-6 h-6 text-emerald-600" />
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">WhatsApp Gateway (Beta)</h2>
                        <p className="text-sm text-gray-500">Conecta tu propio número para enviar notificaciones automáticas</p>
                    </div>
                </div>
            </div>

            <div className="p-8">
                {status === 'CONNECTED' ? (
                    <div className="flex flex-col items-center justify-center py-8 text-center animate-fade-in">
                        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                            <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">¡WhatsApp Conectado!</h3>
                        <p className="text-gray-500 max-w-md mb-6">
                            Tu sistema está listo para enviar confirmaciones y recordatorios usando tu propio número.
                        </p>
                        <button
                            onClick={handleDisconnect}
                            className="text-sm text-red-500 hover:text-red-600 underline"
                        >
                            Desvincular dispositivo
                        </button>
                    </div>
                ) : status === 'QR_READY' && qrCode ? (
                    <div className="flex flex-col items-center justify-center animate-fade-in">
                        <div className="bg-white p-4 rounded-xl border-2 border-emerald-100 shadow-lg mb-6 relative">
                            <img src={qrCode} alt="Scan me" className="w-64 h-64 object-contain" />

                            {/* Scanning Animation line */}
                            <div className="absolute top-0 left-0 w-full h-1 bg-emerald-500 shadow-[0_0_10px_#10b981] animate-scan opacity-50"></div>
                        </div>

                        <div className="flex items-center gap-2 text-emerald-700 font-medium bg-emerald-50 px-4 py-2 rounded-full mb-4">
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            Esperando escaneo...
                        </div>

                        <ol className="text-left text-gray-600 space-y-2 text-sm max-w-xs mx-auto">
                            <li>1. Abre WhatsApp en tu celular</li>
                            <li>2. Ve a Menú {'>'} Dispositivos vinculados</li>
                            <li>3. Toca "Vincular un dispositivo"</li>
                            <li>4. Escanea el código QR</li>
                        </ol>
                    </div>
                ) : (
                    <div className="flex flex-col md:flex-row items-center gap-8">
                        <div className="flex-1 space-y-4">
                            <div className="flex items-start gap-4">
                                <div className="p-3 bg-gray-100 rounded-lg">
                                    <QrCode className="w-6 h-6 text-gray-600" />
                                </div>
                                <div>
                                    <h4 className="font-medium text-gray-900 mb-1">Sin costo por mensaje</h4>
                                    <p className="text-sm text-gray-500">Usa tu plan de datos o WiFi. No pagas fees de API a Meta.</p>
                                </div>
                            </div>

                            {error && (
                                <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg flex items-center gap-2">
                                    <AlertCircle className="w-4 h-4" />
                                    {error}
                                </div>
                            )}

                            <button
                                onClick={handleConnect}
                                disabled={isLoading}
                                className="mt-4 flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        Generando QR...
                                    </>
                                ) : (
                                    <>
                                        <QrCode className="w-5 h-5" />
                                        Conectar WhatsApp
                                    </>
                                )}
                            </button>
                        </div>

                        {/* Visual Illustration */}
                        <div className="hidden md:block w-1/3 opacity-50 grayscale hover:grayscale-0 transition-all duration-500">
                            <div className="bg-gradient-to-tr from-gray-100 to-gray-50 rounded-[2rem] p-4 shadow-xl border border-gray-200">
                                <Smartphone className="w-32 h-32 text-emerald-500 mx-auto" strokeWidth={1.5} />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default WhatsAppIntegration;
